/**
 * Centralized registry for all Global Symbols used across the Magma monorepo.
 * These symbols utilize Symbol.for() to ensure consistency across module boundaries.
 */
/** Global symbol for identifying target objects in the library system */
export const $Target = Symbol.for('$LibraryTarget');
/** Global symbol for marking discoverable entities in the library system */
export const $Discover = Symbol.for('$LibraryDiscover');
/** Global symbol for tracking extensible objects in the library system */
export const $Extensible = Symbol.for('$LibraryExtensible');
/** Global symbol for Node.js custom inspection (used by util.inspect) */
export const $Inspect = Symbol.for('nodejs.util.inspect.custom');
/** Global symbol for identifying logging configuration objects */
export const $LogConfig = Symbol.for('$LibraryLogConfig');
/** Global symbol for accessing the type registry */
export const $Registry = Symbol.for('$LibraryRegistry');
/** Global symbol for marking registered types */
export const $Register = Symbol.for('$LibraryRegister');
/** Global symbol for accessing the serializer registry */
export const $SerializerRegistry = Symbol.for('$LibrarySerializerRegistry');
/** Global symbol for marking class members exempt from immutability enforcement */
export const $Mutable = Symbol.for('$LibraryMutable');
/** Global symbol for brand-checking class instances across module boundaries */
export const $Identity = Symbol.for('$LibraryIdentity');
/** Global symbol for opting out of class decorator subclass wrapping */
export const $Unwrapped = Symbol.for('$LibraryUnwrapped');
export const sym = {
    $Target, $Discover, $Extensible, $Inspect, $LogConfig, $Registry, $Register, $SerializerRegistry, $Identity, $Mutable, $Unwrapped
};
/**
 * @internal
 * Attaches or updates a non-enumerable global symbol on an object if extensible and not already marked with the same value.
 *
 * @param obj - The target object to mark
 * @param symbol - The symbol identifier to attach
 * @param value - Optional payload or boolean marker (default: true)
 * @returns The target object
 * @example
 * ```ts
 * markSymbol(target, sym.$Extensible);
 * ```
 */
export function markSymbol(obj, symbol, value = true) {
    if (obj !== null && (typeof obj === 'object' || typeof obj === 'function') && typeof symbol === 'symbol' && !(symbol in obj && obj[symbol] === value) && Object.isExtensible(obj)) {
        try {
            Object.defineProperty(obj, symbol, { value, enumerable: false, writable: true, configurable: true });
        }
        catch { }
    }
    return obj;
}
/**
 * Identifies and marks an object as a logging configuration object using a global symbol.
 * This allows the library to securely differentiate configs from regular objects.
 *
 * @param obj - The configuration object to mark
 * @returns The marked object
 * @example
 * ```ts
 * const cfg = markConfig({ level: 'debug' });
 * ```
 */
export const markConfig = (obj) => markSymbol(obj, sym.$LogConfig);
/**
 * Marks an object as explicitly extensible using a global symbol to exempt it from recursive deep-freezing.
 *
 * @param obj - The object to mark
 * @returns The marked object
 * @example
 * ```ts
 * const registry = markExtensible([]);
 * ```
 */
export const markExtensible = (obj) => markSymbol(obj, sym.$Extensible);
